import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CaretDown,
  CaretUp,
  CopySimple,
  Envelope,
  Funnel,
  MagicWand,
  PaperPlaneTilt,
  PencilSimple,
  Plus,
  TextAa,
  TrashSimple,
} from '@phosphor-icons/react';
import {
  AlertDialogActions,
  AlertDialogBody,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  useAlertDialog,
} from '@clay-run/glaze/alert-dialog';
import { Badge } from '@clay-run/glaze/badge';
import { Button } from '@clay-run/glaze/button';
import { DebouncedSearchInput } from '@clay-run/glaze/debounced-search-input';
import { Heading } from '@clay-run/glaze/heading';
import { Icon } from '@clay-run/glaze/icon';
import { IconButton } from '@clay-run/glaze/iconbutton';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Text } from '@clay-run/glaze/text';
import { Alert } from '@clay-run/glaze/alert';
import { addErrorToast, addToast } from '@clay-run/glaze/toast/toast-queue';

import { useApi, useCurrentUser } from '../../_shared/hooks';
import { useAppBasePath } from '../../_shared/base-path';
import { GENERATE_AI_BUTTON_WRAPPER_CLASS } from '../ai-accent';
import { isInteractiveRowTarget } from '../row-click-guard';
import { UnsavedWorkGuard, useGuardedFlowEntry, useHasUnsavedWork } from './UnsavedWorkGuard';
import { openGenerateSession, useGenerateWithAIStore } from '../generate-with-ai-store';
import type { GongFlowSummary } from '../../_shared/flow/gong';
import { getLibrary, getSegmentationUsage, getSequenceUsage } from '../api-client';
import { flowContextPath } from '../play-flow-context';
import {
  createPlaybook,
  createSegmentation,
  createSequence,
  duplicatePlaybook,
  duplicateSegmentation,
  duplicateSequence,
  deletePlaybook,
  deleteSegmentation,
  deleteSequence,
  renamePlaybook,
  renameSegmentation,
  renameSequence,
  saveSegmentationConditions,
} from '../mutations';
import { createSequenceFromGongFlow, GongSequencePartialImportError } from '../gong-flow-picker-api';
import { channelBadgeColor, configRowKindBadgeColor } from '../subtype-badge-colors';
import {
  filterPlaybooksLibrary,
  getConfigRowMenuItems,
  isLauncherAdmin,
  parseConfigExpandParam,
} from '../config-page-logic';
import type { PlaybooksConfigRowAction, PlaybooksConfigRowKind } from '../config-page-logic';
import type {
  LauncherChannel,
  PlaybooksLibraryResponse,
  PlaybooksPlaybookSummary,
  PlaybooksSegmentationSummary,
  PlaybooksSequenceSummary,
  PlaybooksUsagePlace,
  PlaybooksUsagePlay,
} from '../schemas';
// v1 data constant, not a component — plain array reused for real product
// copy fidelity ("Terra Co-Pilot" is the actual label, not something to
// re-derive). Key Decision 2 forbids sharing RENDER components between v1
// and v2, not plain data; see this file's channel-label section below.
import { CHANNELS } from '../../_shared/launcher-copy-tokens';
import { renameWarningText } from '../sequence-card-menu';
import { executeSettingsUpdateAll, forkSequenceWithSettings } from '../sender-decision';
import { useUsageSidebarDecision } from '../use-usage-sidebar-decision';
import {
  openUsageSidebar,
  originKeyOf,
  selectUsageSidebar,
  useUsageSidebarStore,
  type PendingUsageSidebar,
} from '../usage-sidebar-store';
import {
  libraryQueryKey,
  matchedMembersPrefixKey,
  segmentationDetailQueryKey,
  segmentationUsageQueryKey,
  sequenceUsageQueryKey,
} from '../query-keys';
import { usePlaysLibrary } from '../use-plays-library';
import { filterPlaysLibrary, mergePlaybookNames } from '../plays-logic';
import { KebabMenu } from './KebabMenu';
import { NameDrawerField } from './NameDrawerField';
import { PlaysLibrarySection } from './PlaysLibrarySection';
import { PushDrawer } from './PushDrawer';
import { SequenceCopyPane } from './SequenceCopyPane';
import { SequenceCreateFields } from './SequenceCreateFields';
import { SegmentationConditionsEditor } from './play-flow/SegmentationConditionsEditor';
// U9 (SN1) — the sequence's own sender control (the relocated, rebound
// `InlineSenderPopover`) and the shared label map it renders through.
import { SequenceFlowIdPopover } from './SequenceFlowIdPopover';
import { SequenceInflectionPopover } from './SequenceInflectionPopover';
import { SequenceSenderPopover } from './SequenceSenderPopover';
import { senderLabel } from '../sender-choice';

/**
 * Launcher V2 config page (plan U3): Playbooks / Plays / Segmentations /
 * Sequences, merged from the prototype's `renderLanding()` (Playbooks) +
 * `renderLibrary()` (Segmentations/Sequences, v6 prototype lines 5136–5220)
 * into four stacked sections on ONE page — every Phase-2 version-chrome
 * element (version pins, "N pinned behind", History/divergence tabs, the
 * publish-new-version alert, "View usage") stripped per the plan's Scope
 * Boundaries. Rows keep: name, a type/channel badge, a real head-version
 * count, and a kebab (Edit / Rename / Duplicate / Delete).
 *
 * "Plays" (2026-09-17 QA follow-ups, U1) is a fourth section, between
 * Playbooks and Segmentations — its own `GET /v2/plays/library` read
 * (`use-plays-library.ts`), grouped by campaign, deliberately separate from
 * the other three sections' `GET /v2/library` query.
 *
 * ── "Used in N places" badges ─────────────────────────────────────────────
 * Sequence and Segmentation rows show a real "Used in N places" count from
 * `usageCount` on their own library summary — see
 * `plans/2026-09-09-001-launcher-v2-round3-qa-fixes-plan.md`, decision 11.
 */

// ─────────────────────────────────────────────────────────────────────────────
// `filterPlaybooksLibrary` / `isLauncherAdmin` / `getConfigRowMenuItems` live
// in `../config-page-logic.ts` (a Glaze/React-free leaf) — see that file's
// head-of-file note for why: this file transitively imports `KebabMenu.tsx`
// -> `@clay-run/glaze/dropdown`, whose module top-level crashes under
// `bun:test`'s DOM-less runtime, so `__tests__/v2-config-page.test.tsx`
// cannot import these functions from here.
// ─────────────────────────────────────────────────────────────────────────────

/** `Channel -> display label`, from v1's real `CHANNELS` copy (`Terra Co-Pilot` for `slack_copilot`, etc.) — not re-invented here. */
const CHANNEL_LABEL_BY_VALUE: Record<string, string> = Object.fromEntries(
  CHANNELS.map((c) => [c.value, c.label]),
);

function channelLabel(channel: string): string {
  return CHANNEL_LABEL_BY_VALUE[channel] ?? channel;
}

/** Round-3-QA-fixes plan, decision 11 — "Used in N places" / "Used in 1 place" / "Not used yet", from `usageCount`. */
function usagePlacesBadgeText(usageCount: number): string {
  if (usageCount === 0) return 'Not used yet';
  if (usageCount === 1) return 'Used in 1 place';
  return `Used in ${usageCount} places`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Rename — PushDrawer + a stub-backed inline field (plan approach: "rename
// goes through the kebab into a PushDrawer ... a stub-backed inline field is
// acceptable staging").
//
// Not `useDraftState`: `_shared/hooks/useDraftState.ts` DOES now re-export
// the real `web/app-home/src/hooks/useDraftState` (Launcher V2 A2 U5's
// QualifyStep Audience Rule editor was the first mini-app consumer;
// `SegmentationConditionsEditor.tsx` is the second, as of Playbooks QA
// round 6, U4), so that stale "no re-export yet" reason is gone. This page
// is outside the v4 SSE tree, but `terra/no-effect-reseeded-draft` applies to
// every screen: any reseed effect added here is flagged.
// Instead: a `key={target.id}`-remounted local `useState` seeded once per
// open target — the sanctioned alternative to a bare "useState + reseed
// effect" (a fresh key forces a fresh mount instead of an effect racing a
// prop change).
// ─────────────────────────────────────────────────────────────────────────────

interface RenameTarget {
  kind: PlaybooksConfigRowKind;
  id: string;
  name: string;
  /**
   * U1 (R-D4) — only threaded for `kind === 'sequence'` (the rename-warning
   * requirement is sequence-specific); `undefined` for playbook/segmentation
   * targets, which never show the warning line.
   */
  usageCount?: number;
}

// `NameDrawerField` moved to `./NameDrawerField.tsx` (U1, R-L3) — shared with
// `SequencesStep.tsx`'s in-play Rename drawer so both go through the exact
// same field instead of each owning a copy.

// ─────────────────────────────────────────────────────────────────────────────
// Sections
// ─────────────────────────────────────────────────────────────────────────────

const SECTION_CARD_CLASS = 'rounded-lg border border-border-primary bg-bg-primary';

interface SectionShellProps {
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
  count: number;
  create?: { label: string; onClick: () => void };
  children: React.ReactNode;
}

function SectionShell({ title, icon: SectionIcon, count, create, children }: SectionShellProps) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        {SectionIcon && <Icon type={SectionIcon} />}
        <Heading size="lg">{title}</Heading>
        <Badge>{count}</Badge>
        {create && (
          <Button variant="ghost" size="xs" aria-label={create.label} onClick={create.onClick}>
            <Icon type={Plus} />
          </Button>
        )}
      </div>
      <div className={SECTION_CARD_CLASS}>{children}</div>
    </section>
  );
}

/** Exported for `PlaysLibrarySection.tsx` (U1, 2026-09-17) — a two-line leaf; exporting beats a third copy. */
export function EmptySectionRow({ hasSearch, kind }: { hasSearch: boolean; kind: string }) {
  return (
    <div className="px-4 py-8 text-center">
      <Text size="sm" variant="tertiary">
        {hasSearch ? `No ${kind} match your search.` : `No ${kind} yet.`}
      </Text>
    </div>
  );
}

/**
 * QA follow-ups U5 (item 21 / R15, per D5) — the Library page's rows previously
 * had NO expand support at all (name/badges/menu only; grepped directly, no
 * pre-existing expansion affordance to reuse). `expand` is an optional prop
 * so a Playbook row (whose "Edit" navigates straight to the playbook-mode
 * flow, U11 — an inline content editor for it is a different PR's scope, not
 * this one) renders visually and behaviorally unchanged (no caret, no expand
 * body); Segmentation rows opt in with the inline conditions editor, and
 * Sequence rows opt in as of E1 (step 2/3) with `SequenceCopyPane`.
 *
 * Mirrors the prototype's `libTgRow()` shape (`playbooks-prototype-v6.html`
 * `:5182-5277`): a caret toggles the row open onto an inline body, never a
 * drawer — the same "conditions are edited in place, never in a sidebar"
 * decision U4 already applied to the rung waterfall (`:8418`), extended here
 * to the Library entry point so a segmentation attached to zero rungs is no
 * longer uneditable.
 */
interface RowShellExpand {
  expanded: boolean;
  onToggleExpand: () => void;
  body: React.ReactNode;
}

interface RowShellProps {
  name: string;
  badges: React.ReactNode;
  menu: React.ReactNode;
  expand?: RowShellExpand;
  /**
   * Playbooks QA round 4, U9 (SN1) — an optional SECOND line under the
   * name/badges row, inside the same `min-w-0 flex-1` column. Sequence rows
   * use it for `Sender: X` + its popover trigger; the plan's own
   * Deferred-to-Implementation default picked this slot because it is the same
   * one the play-flow's sequence header uses for its own Gong-flow/Sender
   * sub-lines, so the two surfaces read the same way top-down.
   *
   * Optional and additive: every row that omits it renders byte-identically to
   * before this unit.
   */
  subline?: React.ReactNode;
}

function RowShell({ name, badges, menu, expand, subline }: RowShellProps) {
  /**
   * Live-QA fixes, A2 — whole-header click-to-expand. Only wired when
   * `expand` is passed: a Playbook row (no `expand` prop — its "Edit"
   * navigates away instead, per this component's own doc comment above) has
   * nothing to toggle, so it stays a plain, non-interactive header rather
   * than gaining a pointer cursor and a toggle that does nothing.
   */
  const handleHeaderClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!expand) return;
    if (isInteractiveRowTarget(e.target)) return;
    expand.onToggleExpand();
  };
  const handleHeaderKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!expand) return;
    if (e.key !== 'Enter' && e.key !== ' ') return;
    if (isInteractiveRowTarget(e.target)) return;
    e.preventDefault();
    expand.onToggleExpand();
  };

  return (
    <div className="flex flex-col">
      <div
        className={`flex items-center gap-3 px-4 py-3 ${expand ? 'cursor-pointer' : ''}`}
        tabIndex={expand ? 0 : undefined}
        onClick={handleHeaderClick}
        onKeyDown={handleHeaderKeyDown}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Text size="sm" weight="semibold" className="truncate">
              {name}
            </Text>
            {badges}
          </div>
          {subline && <div className="mt-1 flex flex-wrap items-center gap-1.5">{subline}</div>}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {menu}
          {expand && (
            <IconButton
              icon={expand.expanded ? CaretUp : CaretDown}
              variant="ghost"
              size="xs"
              aria-label={expand.expanded ? 'Collapse' : 'Expand'}
              data-row-control
              onClick={(e) => {
                e.stopPropagation();
                expand.onToggleExpand();
              }}
            />
          )}
        </div>
      </div>
      {expand?.expanded && (
        <div className="border-t border-border-secondary bg-bg-secondary px-4 py-4">{expand.body}</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

export function PlaybooksConfigPage() {
  const { apiFetch } = useApi();
  const { roles } = useCurrentUser();
  const isAdmin = isLauncherAdmin(roles);
  const queryClient = useQueryClient();
  const { openAlertDialog, closeAlertDialog } = useAlertDialog();
  const basePath = useAppBasePath();

  const query = useQuery({
    queryKey: libraryQueryKey(),
    queryFn: () => getLibrary(apiFetch),
    // This page shows its own more-informative inline Alert + toast on
    // failure (below) — suppress the generic global toast so a failure
    // doesn't double up two toasts in two different visual systems (Codex
    // adversarial-review fix; `mini-apps/CLAUDE.md` lane-1 convention). This
    // query is v2-only (not shared with v1), so it's safe to opt out here
    // directly rather than threading an option through a shared hook.
    meta: { suppressErrorToast: true },
  });

  const [search, setSearch] = useState('');
  const [renameTarget, setRenameTarget] = useState<RenameTarget | null>(null);
  const [savingRename, setSavingRename] = useState(false);
  /**
   * U5's minimal "+ New" entry point: which kind's create drawer is open, if
   * any. `'sequence'` joins the union as of E1 step 3 — Sequence CRUD's UI
   * gap this whole unit closes.
   */
  const [createKind, setCreateKind] = useState<'segmentation' | 'playbook' | 'sequence' | null>(null);
  const [savingCreate, setSavingCreate] = useState(false);
  /**
   * U5 (item 21 / R15) — which Segmentation rows have their inline conditions
   * editor open. A `Set`, not a single id, matching `RungRow`'s per-row
   * `expanded` state (U4): each row owns its own toggle independently, so
   * opening a second row's editor never closes the first.
   */
  const [expandedSegmentationIds, setExpandedSegmentationIds] = useState<Set<string>>(new Set());
  /**
   * E1 (step 3) — the Sequence rows' equivalent of `expandedSegmentationIds`:
   * a caret opens `SequenceCopyPane` in place. Same per-row-independent `Set`
   * shape, one instance per identity kind rather than a single shared map, so
   * neither section's expand state can collide with the other's ids.
   */
  const [expandedSequenceIds, setExpandedSequenceIds] = useState<Set<string>>(new Set());
  /**
   * `SegmentationConditionsEditor` (U4) is deliberately host-agnostic and
   * exposes no "revert to loaded state" affordance of its own — only
   * `onSaved`/`onCancel`. Its draft seeds ONCE per mount from the query
   * cache (host-agnostic-editor doc comment), so Revert here is implemented
   * as a host-side remount: bumping a segmentation's nonce changes the
   * editor's React `key`, forcing a fresh instance that re-seeds from the
   * still-cached (unedited, since no save has happened) head conditions.
   * Never patch/restructure the editor for this — see this unit's plan
   * note ("do NOT rebuild or restructure it").
   */
  const [segmentationEditorNonce, setSegmentationEditorNonce] = useState<Record<string, number>>({});
  /**
   * Live-QA fixes, A5 — which Segmentation rows' inline editor currently has
   * an unsaved (dirty) draft, fed by `SegmentationConditionsEditor`'s
   * `onDirtyChange`. Drives the Revert button's `disabled` state: reverting a
   * pristine editor has nothing to discard, so it must not render active.
   */
  const [dirtySegmentationIds, setDirtySegmentationIds] = useState<Set<string>>(new Set());

  /**
   * U1 — the sidebar's `originUrl` for a `'library'` origin points back
   * here as `?expand=<sequence|segmentation>:<id>` (`usage-sidebar-logic
   * .ts`'s `originUrl`). Applied ONCE on mount, not on every
   * `searchParams` change (Resolved note on the bead: "read once — a
   * later in-page search-param change is not re-applied") — `appliedRef`
   * resets at the TOP of the mount effect (StrictMode rule, same pattern
   * as `QualifyStep.tsx`'s `autoExpandAppliedRef`), so this still applies
   * on the "real" post-StrictMode-double-invoke mount, not just the first
   * one. The id is added to the expanded set regardless of whether the
   * library query has resolved yet — rows are keyed by id, so a row that
   * hasn't arrived yet still renders expanded once it does.
   */
  const [searchParams] = useSearchParams();
  const appliedExpandParamRef = useRef(false);
  useEffect(() => {
    appliedExpandParamRef.current = false;
  }, []);
  useEffect(() => {
    if (appliedExpandParamRef.current) return;
    const parsed = parseConfigExpandParam(searchParams.get('expand'));
    if (parsed === null) return;
    appliedExpandParamRef.current = true;
    if (parsed.kind === 'sequence') {
      setExpandedSequenceIds((prev) => (prev.has(parsed.id) ? prev : new Set(prev).add(parsed.id)));
    } else {
      setExpandedSegmentationIds((prev) => (prev.has(parsed.id) ? prev : new Set(prev).add(parsed.id)));
    }
  }, []);

  useEffect(() => {
    if (query.isError) {
      addErrorToast({ content: 'Failed to load the library. Please try again.' });
    }
  }, [query.isError]);

  const filtered = useMemo(
    () => (query.data ? filterPlaybooksLibrary(query.data, search) : null),
    [query.data, search],
  );

  // U1 (2026-09-17 QA follow-ups) — the Library's "Plays" section, its own
  // read (NOT a widening of the campaign-scoped GET /plays), filtered by the
  // SAME search box the other three sections already share.
  const playsLibrary = usePlaysLibrary();
  // F7 / QA round 7, U1 — the library's map first (it is what every other
  // section renders from), then the plays read's names for playbooks the
  // library no longer lists (archived). Never the other way round. Shared
  // with Home, the Calendar and `PlayFlowShell`'s heading via
  // `plays-logic.ts#mergePlaybookNames` — this used to be built inline here.
  const playbookNameById = useMemo(
    () => mergePlaybookNames(query.data?.playbooks, playsLibrary.data?.playbookNames),
    [query.data, playsLibrary.data],
  );
  const filteredPlays = useMemo(
    () => filterPlaysLibrary(playsLibrary.data?.campaigns ?? [], search, playbookNameById),
    [playsLibrary.data, search, playbookNameById],
  );

  /**
   * Round-4 QA codex-review fix (wave 1, findings 3 and 5) — this page has
   * TWO independent sources of unsaved work, and only one of them was
   * covered.
   *
   * `dirtySegmentationIds` is the component-local condition-editor dirt this
   * page already tracked (fed by `onDirtyChange`). `useHasUnsavedWork()` is
   * the other half: `SequenceCopyPane`, this page's inline sequence editor,
   * writes to the SAME module-scope `sequence-copy-draft-store` the Play flow
   * uses (`draftScope="library"`), so editing a Config sequence's copy and
   * then closing the tab produced no warning at all.
   *
   * `guardedNavigate` covers the other blind spot: `handleEdit` and the
   * post-create playbook redirect below call `navigate()` directly, which the
   * guard's capture-phase click listener never sees (no anchor, no click).
   * `draftsSurvive: false` here because this page's condition drafts really
   * do die on the unmount — the plan's one accepted U7 gap — so the dialog
   * must not offer a "keep edits" option it cannot honor.
   *
   * R5-3 (playbooks-qa-round5-feedback.md) — scoped to `'library'` (the same
   * literal `SequenceCopyPane` passes as its `draftScope`), NOT the unscoped
   * whole-store read this used to be. An unrelated `rung:*`/`new:*` draft
   * left alive by the Play flow's deliberate Back-button survival (or by a
   * pre-fix `SequenceCopyPane` leak — see that file's own fix) made THIS
   * page read dirty for edits the rep never made here. The Play flow's own
   * `useHasUnsavedWork()` call stays unscoped — its drafts genuinely span
   * rung scopes.
   *
   * QA round 7, U2 (item 2) — a THIRD blind spot, closed by swapping
   * `useGuardedNavigate` for {@link useGuardedFlowEntry} with these SAME
   * options: surviving Play-flow drafts (a rung/new-scoped copy draft kept
   * alive by the Back-button design) that this navigation's target flow
   * would wipe. `useGuardedFlowEntry` checks that on top of `configDirty`
   * below — it does not change what `configDirty` itself means.
   */
  // Called unconditionally, THEN combined — `a || useHasUnsavedWork()` would
  // short-circuit the hook call and break the rules of hooks.
  const hasStoreUnsavedWork = useHasUnsavedWork('library');
  const configDirty = dirtySegmentationIds.size > 0 || hasStoreUnsavedWork;
  // Codex review of Playbooks QA round 5 (finding B1) — `discardScope:
  // 'library'` so this page's own "Discard changes" click can only ever
  // clear ITS OWN scope's copy drafts/generated-copy reviews, matching the
  // detection above (`useHasUnsavedWork('library')`) instead of the global
  // wipe `discardAllUnsavedWork()` used to always perform.
  const guardedNavigate = useGuardedFlowEntry({
    dirty: configDirty,
    draftsSurvive: false,
    discardScope: 'library',
  });

  const updateLibraryRowName = useCallback(
    (kind: PlaybooksConfigRowKind, id: string, name: string) => {
      queryClient.setQueryData<PlaybooksLibraryResponse>(libraryQueryKey(), (prev) => {
        if (!prev) return prev;
        const rename = <T extends { id: string; name: string }>(rows: T[]): T[] =>
          rows.map((row) => (row.id === id ? { ...row, name } : row));
        return {
          segmentations: kind === 'segmentation' ? rename(prev.segmentations) : prev.segmentations,
          sequences: kind === 'sequence' ? rename(prev.sequences) : prev.sequences,
          playbooks: kind === 'playbook' ? rename(prev.playbooks) : prev.playbooks,
        };
      });
    },
    [queryClient],
  );

  /**
   * Playbooks QA round 4, U9 (SN1) — the sender twin of
   * {@link updateLibraryRowName}: patches ONE sequence row's identity fields
   * in the cached library so the row's `Sender: X` (and, playbooks-
   * inflection-wiring 2026-09-17, `Flow ID:`/`CC:`/`Reply-to:`) lines update
   * the instant the matching popover's write lands, without waiting for the
   * invalidation's refetch. Sequence-only by construction — segmentations and
   * playbooks have none of these fields. Generalized from the original
   * sender-only `updateLibraryRowSender` so one function covers all four
   * identity popovers instead of a near-duplicate per field.
   */
  const updateLibrarySequenceRow = useCallback(
    (id: string, patch: Partial<Pick<PlaybooksSequenceSummary, 'sender' | 'flowId' | 'cc' | 'replyTo'>>) => {
      queryClient.setQueryData<PlaybooksLibraryResponse>(libraryQueryKey(), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          sequences: prev.sequences.map((row) => (row.id === id ? { ...row, ...patch } : row)),
        };
      });
    },
    [queryClient],
  );

  const invalidateLibrary = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: libraryQueryKey() });
  }, [queryClient]);

  const handleRenameSave = useCallback(
    async (name: string) => {
      if (!renameTarget) return;
      setSavingRename(true);
      try {
        if (renameTarget.kind === 'segmentation') {
          await renameSegmentation(apiFetch, renameTarget.id, name);
        } else if (renameTarget.kind === 'sequence') {
          await renameSequence(apiFetch, renameTarget.id, name);
        } else {
          await renamePlaybook(apiFetch, renameTarget.id, name);
        }
        // A rename is persisted for all three kinds (Sequence rename went
        // real in PR C / U4B), but the cached library row is still patched in
        // place first so the list updates without a refetch round-trip; the
        // invalidation below reconciles it.
        updateLibraryRowName(renameTarget.kind, renameTarget.id, name);
        invalidateLibrary();
        addToast({ content: `Renamed to "${name}".`, status: 'success' });
        setRenameTarget(null);
      } catch {
        addErrorToast({ content: 'Could not rename — try again.' });
      } finally {
        setSavingRename(false);
      }
    },
    [apiFetch, renameTarget, updateLibraryRowName, invalidateLibrary],
  );

  /**
   * E1 (step 3) — Sequence create needs a `channel` alongside `name`
   * (`createSequence`'s required, identity-INVARIANT field — see
   * `SequenceCreateFields`'s own doc), so this callback's signature grew an
   * optional third argument rather than forking into a second handler; the
   * Segmentation/Playbook branches simply never pass it.
   *
   * Round-3-QA-fixes plan (decision 5) — a Gong channel additionally carries
   * a picked `flow`; that branch calls `createSequenceFromGongFlow` instead
   * of the plain `createSequence`. Its failure ordering (C2's doc: fetch
   * steps → bind → save, all AFTER `createSequence` itself has already
   * committed) means a `GongSequencePartialImportError` means a real
   * sequence now sits in the library, unbound or bound-but-empty — never
   * "nothing happened" — so THAT specific error shows a different, accurate
   * toast and skips the generic one below rather than re-throwing into it.
   *
   * Codex adversarial-review finding F4 — a PRE-create failure (`createSequence`
   * itself rejects, inside `createSequenceFromGongFlow`) propagates as an
   * ordinary error, never `GongSequencePartialImportError` (see that class's
   * own doc) — nothing was created, so it must NOT be caught here. It falls
   * through to the outer `catch` below, which shows a generic toast and
   * leaves the form open (no `setCreateKind(null)`) so the rep can retry
   * without re-entering the name/flow. Before this fix, EVERY rejection from
   * `createSequenceFromGongFlow` — pre-create included — was caught here
   * identically, closing the form and telling the rep something was created
   * when nothing was.
   */
  const handleCreateSave = useCallback(
    async (
      name: string,
      channel?: LauncherChannel,
      // Playbooks QA round 4, U9 (SN1) — the sequence's locked sender,
      // collected by `SequenceCreateFields`. `null`/omitted = "Not set".
      // Ignored for segmentation/playbook creates, which have no sender.
      sender?: string | null,
      flow?: GongFlowSummary,
    ) => {
      if (!createKind) return;
      setSavingCreate(true);
      try {
        let createdId: string;
        if (createKind === 'segmentation') {
          createdId = (await createSegmentation(apiFetch, name)).id;
        } else if (createKind === 'sequence') {
          // `SequenceCreateFields` never calls `onSave` without a channel
          // (its own Save button is gated on one being picked) — this is
          // defense in depth, not a reachable rep-facing state.
          if (!channel) throw new Error('Pick a channel.');
          if (channel === 'gong_engage') {
            // Same defense-in-depth reasoning as the missing-channel check
            // above — `SequenceCreateFields`'s Save is gated on a picked
            // flow whenever Gong is selected.
            if (!flow) throw new Error('Pick a Gong flow.');
            try {
              createdId = (
                await createSequenceFromGongFlow(apiFetch, { flow, name, sender: sender ?? null })
              ).sequenceId;
            } catch (err) {
              if (!(err instanceof GongSequencePartialImportError)) {
                // Pre-create failure — nothing was created. Fall through to
                // the outer catch's generic toast, and leave the form open.
                throw err;
              }
              invalidateLibrary();
              addErrorToast({
                content: `Created "${name}" but couldn't load its steps from Gong. Open it in a play and use "Load steps from Gong".`,
              });
              setCreateKind(null);
              return;
            }
          } else {
            createdId = (await createSequence(apiFetch, name, channel, sender ?? null)).id;
          }
        } else {
          createdId = (await createPlaybook(apiFetch, name)).id;
        }
        invalidateLibrary();
        // Auto-expand the new Sequence row (plan E1 step 3) — its
        // `SequenceCopyPane` fetches by id and needs zero seed content to
        // render (an empty-steps v1, plus the "Add step" doorway).
        if (createKind === 'sequence') {
          setExpandedSequenceIds((prev) => {
            const next = new Set(prev);
            next.add(createdId);
            return next;
          });
        }
        addToast({ content: `Created "${name}".`, status: 'success' });
        setCreateKind(null);
        // U11 — a brand-new Playbook has no content to show on this page (no
        // inline expand, per `RowShell`'s doc comment above); it lands the
        // rep straight in the flow instead, seedless (U10's picker is what
        // asks for a campaign, not this URL).
        if (createKind === 'playbook') {
          // Round-4 QA codex-review fix (wave 1, finding 5) — through the
          // guard, not a bare `navigate`: a rep who had a segmentation
          // condition draft open and then created a playbook was taken
          // straight out of this page and lost it with no warning at all.
          guardedNavigate(
            flowContextPath(basePath, { kind: 'playbook', playbookId: createdId, seedCampaignSfdcId: null }),
          );
        }
      } catch (err) {
        addErrorToast({
          content: err instanceof Error ? err.message : 'Could not create — try again.',
        });
      } finally {
        setSavingCreate(false);
      }
    },
    [apiFetch, basePath, createKind, invalidateLibrary, guardedNavigate],
  );

  /**
   * U5 (item 21 / R15) — Segmentation rows: "Edit" toggles the SAME inline
   * conditions editor U4 built for the rung waterfall, expanded directly in
   * the row (D5). Sequence rows get their own equivalent toggle as of E1
   * step 3 (`SequenceCopyPane`); Playbook rows have no inline expand at all
   * (U11) — their "Edit" navigates straight to the playbook-mode flow
   * instead (`handleEdit` below), so there is nothing here for them to
   * toggle.
   */
  const toggleSegmentationExpanded = useCallback((id: string) => {
    setExpandedSegmentationIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  /** E1 (step 3) — Sequence rows' expand toggle, mirroring `toggleSegmentationExpanded` above. */
  const toggleSequenceExpanded = useCallback((id: string) => {
    setExpandedSequenceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  /**
   * Round-2-followup-codex-fixes plan, Unit B4 (finding 5, DECIDED 2026-09-03
   * — "follow-the-duplicate"): `SequenceCopyPane`'s "Save as new copy" has no
   * row of its own to re-key — that's this page's job. Two things happen in
   * one place: (a) an optimistic insert of the synthesized duplicate `summary`
   * into the `library` query's cached `sequences` array, GUARDED against a
   * double-insert if the real invalidation-triggered refetch already landed
   * first (`SequenceCopyPane`'s own `invalidateQueries` call races this); (b)
   * `expandedSequenceIds` swaps the original id for the new one in one
   * `setState`, so exactly one Sequence row ends up expanded and it's the
   * duplicate's — collapsing the original's row (it reappears, collapsed and
   * untouched, once the real refetch lands) and mounting a FRESH
   * `SequenceCopyPane` keyed by the duplicate's own id in its place.
   */
  const handleSequenceSavedAsNewCopy = useCallback(
    (originalSequenceId: string, newSequenceId: string, summary: PlaybooksSequenceSummary) => {
      queryClient.setQueryData<PlaybooksLibraryResponse>(libraryQueryKey(), (prev) => {
        if (!prev) return prev;
        if (prev.sequences.some((s) => s.id === newSequenceId)) return prev;
        return { ...prev, sequences: [...prev.sequences, summary] };
      });
      setExpandedSequenceIds((prev) => {
        const next = new Set(prev);
        next.delete(originalSequenceId);
        next.add(newSequenceId);
        return next;
      });
    },
    [queryClient],
  );

  const revertSegmentationConditions = useCallback((id: string) => {
    setSegmentationEditorNonce((prev) => ({ ...prev, [id]: (prev[id] ?? 0) + 1 }));
    // Remounting re-seeds from the still-cached (unedited) head, so this
    // row's draft is about to become pristine again — clear it eagerly
    // rather than waiting on the remounted editor's own dirty effect to
    // catch up, so Revert re-disables immediately.
    setDirtySegmentationIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const handleSegmentationDirtyChange = useCallback((id: string, dirty: boolean) => {
    setDirtySegmentationIds((prev) => {
      const has = prev.has(id);
      if (dirty === has) return prev;
      const next = new Set(prev);
      if (dirty) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  // `getConfigRowMenuItems` only emits the 'edit' action for kind === 'playbook'
  // (segmentation/sequence rows dropped it — their row header click already
  // expands/collapses, so Edit was a dead duplicate of that toggle). U11 —
  // Edit no longer has a row-local destination at all: it navigates straight
  // to the playbook-mode flow (`flowContextPath`), seedless (a playbook opens
  // with no campaign preselected; the rep picks one via U10's seed picker).
  // Round-4 QA codex-review fix (wave 1, finding 5) — `guardedNavigate`, not
  // `navigate`: this is a kebab-menu action, not an anchor, so the guard's
  // capture-phase click listener never sees it.
  const handleEdit = useCallback(
    (id: string) => {
      guardedNavigate(
        flowContextPath(basePath, { kind: 'playbook', playbookId: id, seedCampaignSfdcId: null }),
      );
    },
    [basePath, guardedNavigate],
  );

  const handleDuplicate = useCallback(
    async (kind: PlaybooksConfigRowKind, id: string) => {
      try {
        if (kind === 'segmentation') await duplicateSegmentation(apiFetch, id);
        else if (kind === 'sequence') await duplicateSequence(apiFetch, id);
        else await duplicatePlaybook(apiFetch, id);
        invalidateLibrary();
        addToast({ content: 'Duplicated.', status: 'success' });
      } catch (err) {
        addErrorToast({ content: err instanceof Error ? err.message : 'Could not duplicate — try again.' });
      }
    },
    [apiFetch, invalidateLibrary],
  );

  /**
   * U2b — no longer closes anything itself (bead decision: "drops
   * `closeAlertDialog()` in favor of the hook's close"). Each of this
   * function's two callers below owns its own closing/toasting because they
   * disagree on WHAT closes on success and WHO shows the failure toast:
   * the Playbook `AlertDialog`'s own Danger button (still `useAlertDialog`,
   * round-3 decision 8 — untouched by this unit) closes itself and toasts
   * on catch; the Sequence/Segmentation `delete` decision handler
   * (registered below via `useUsageSidebarDecision`) lets a thrown error
   * propagate so `use-usage-sidebar-decision.ts`'s own generic catch shows
   * the toast and leaves the sidebar open with the button re-enabled — this
   * function must not ALSO toast, or that path double-toasts.
   */
  const handleDeleteConfirm = useCallback(
    async (kind: PlaybooksConfigRowKind, id: string): Promise<void> => {
      if (kind === 'segmentation') await deleteSegmentation(apiFetch, id);
      else if (kind === 'sequence') await deleteSequence(apiFetch, id);
      else await deletePlaybook(apiFetch, id);
      // Soft delete: the row's `archived_at` is stamped and `GET /v2/library`
      // already filters archived rows out, so the invalidation below is what
      // actually removes it from the list.
      invalidateLibrary();
    },
    [apiFetch, invalidateLibrary],
  );

  /**
   * U2b — the Sequence/Segmentation half of delete now settles through the
   * "Where is this used" sidebar's `delete` decision, exactly like a save's
   * "Update all"/"Save a new version" (`SequenceCopyPane.tsx`'s own hook).
   * This page has no single fixed target to bind a static origin key to
   * (unlike `SequenceCopyPane`, which only ever deletes/saves its OWN
   * `sequenceId`) — a delete can be requested for ANY row on this page, so
   * the origin key this hook matches against is read live off whatever
   * `delete`-mode request is currently pending, and is the empty string
   * (matches nothing) whenever nothing is pending or the pending request is
   * a `save` — the mode a `SequenceCopyPane` row's own gate opened, which
   * this page's hook must never accidentally consume instead of that row's
   * own `updateAll`/`saveNew` handlers.
   */
  /**
   * Playbooks QA round 4, U6 (S2) / Decision 13 — "Update all" for a library
   * segmentation's conditions. Writes the rep's draft onto the SHARED head
   * acknowledging the FRESH `pending.places.length` (never the frozen
   * `acknowledgedCount` — a `shared_mapping_conflict` retry updates `places`
   * and deliberately leaves that field alone), then runs the same
   * invalidation set `SegmentationConditionsEditor`'s in-place save path does.
   */
  const handleSegmentationUpdateAll = useCallback(
    async (pending: PendingUsageSidebar, isStillCurrent: () => boolean): Promise<void> => {
      if (pending.target.kind !== 'segmentation') return;
      const conditions = pending.draftConditions;
      if (conditions == null || pending.headVersionId === null) return;
      await saveSegmentationConditions(
        apiFetch,
        pending.target.id,
        conditions,
        pending.headVersionId,
        pending.places.length,
      );
      if (!isStillCurrent()) return;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: segmentationDetailQueryKey(pending.target.id) }),
        queryClient.invalidateQueries({ queryKey: libraryQueryKey() }),
        queryClient.invalidateQueries({ queryKey: matchedMembersPrefixKey() }),
      ]);
      addToast({ content: 'Conditions saved.', status: 'success' });
    },
    [apiFetch, queryClient],
  );

  /**
   * U6 (S2) — "Save a new version" from the LIBRARY doorway. There is no rung
   * to remap here (that is the whole difference from `QualifyStep`'s own
   * handler), so the fork is surfaced the way this page already surfaces a
   * sequence fork: append the new row to the library cache and expand it,
   * collapsing the original — `handleSequenceSavedAsNewCopy`'s pattern, with
   * a real invalidation behind it since a segmentation summary carries
   * `usageCount`/`isMapped`/`conditions` this page cannot synthesize
   * correctly for a brand-new identity.
   *
   * Toast copy per the plan's "Deferred to Implementation" default for U6:
   * `Saved a new version.` — there is no rung to name from here.
   */
  const handleSegmentationSaveNew = useCallback(
    async (pending: PendingUsageSidebar, isStillCurrent: () => boolean): Promise<void> => {
      if (pending.target.kind !== 'segmentation') return;
      const conditions = pending.draftConditions;
      if (conditions == null) return;
      const created = await duplicateSegmentation(apiFetch, pending.target.id, {
        conditions,
        expectedSourceVersionId: pending.headVersionId ?? undefined,
      });
      if (!isStillCurrent()) return;
      invalidateLibrary();
      setExpandedSegmentationIds((prev) => {
        const next = new Set(prev);
        next.delete(pending.target.id);
        next.add(created.id);
        return next;
      });
      addToast({ content: 'Saved a new version.', status: 'success' });
    },
    [apiFetch, invalidateLibrary],
  );

  /**
   * Playbooks QA round 6, U7 (item 3), widened by plan 2026-09-28-006 U4 to
   * every settings field — "Update all" for a Library-origin SETTINGS save
   * (in practice the row's sender popover; the doorway's CC/flow popovers
   * write in place): the shared `executeSettingsUpdateAll` helper, a Library
   * write with no `playId` (R12 — the doorway edits the master on purpose).
   */
  const handleSettingsUpdateAll = useCallback(
    (pending: PendingUsageSidebar, isStillCurrent: () => boolean): Promise<void> =>
      executeSettingsUpdateAll(apiFetch, queryClient, pending, isStillCurrent),
    [apiFetch, queryClient],
  );

  /**
   * Playbooks QA round 6, U7 (item 3), widened by plan 2026-09-28-006 U4 —
   * "Save as a new sequence" for a Library-origin SETTINGS save. Unlike a
   * rung origin, there is no rung to remap onto — the fork is left
   * unattached, exactly like today's kebab "Duplicate" followed by the
   * field's own write on the copy. Re-keys `expandedSequenceIds` onto the
   * new row via {@link handleSequenceSavedAsNewCopy} (same append-and-expand
   * shape a segmentation fork already uses on this page).
   */
  const handleSettingsSaveNew = useCallback(
    async (pending: PendingUsageSidebar, isStillCurrent: () => boolean): Promise<void> => {
      const draft = pending.draftSettings;
      if (draft === undefined) return;
      const sourceSummary = queryClient
        .getQueryData<PlaybooksLibraryResponse>(libraryQueryKey())
        ?.sequences.find((seq) => seq.id === pending.target.id);
      const created = await forkSequenceWithSettings(apiFetch, queryClient, pending);
      if (!isStillCurrent()) return;
      if (sourceSummary) {
        const summary: PlaybooksSequenceSummary = {
          ...sourceSummary,
          id: created.id,
          name: draft.field === 'name' ? draft.name : `${sourceSummary.name} (copy)`,
          ...(draft.field === 'sender' ? { sender: draft.sender } : {}),
          ...(draft.field === 'inflection' ? { cc: draft.cc, replyTo: draft.replyTo } : {}),
          ...(draft.field === 'flow' ? { flowId: draft.flowId } : {}),
          headVersionId: created.versionId,
          headVersionNumber: created.versionNumber,
          usageCount: 0,
        };
        handleSequenceSavedAsNewCopy(pending.target.id, created.id, summary);
      }
      addToast({
        content:
          draft.field === 'sender'
            ? "Saved a new sequence with this sender. It's in the Library, not mapped anywhere yet."
            : "Saved a new sequence with this change. It's in the Library, not mapped anywhere yet.",
        status: 'success',
      });
    },
    [apiFetch, queryClient, handleSequenceSavedAsNewCopy],
  );

  const pendingUsageSidebar = useUsageSidebarStore(selectUsageSidebar);
  /**
   * Codex fixes 2026-09-17, F5 — a decision on a LIBRARY SEQUENCE save is
   * consumed by that row's own `SequenceCopyPane` hook, and the pane only
   * exists while its row is expanded (`RowShell` mounts `expand.body` when
   * `expanded`). Row collapse is component-local state the sidebar cannot
   * see, and `?expand=` applies once per mount — so a rep who Applied,
   * collapsed the row, then clicked "Keep reviewing instead" (or "Update
   * all") had the decision sit `'running'` with no consumer: footer and X
   * inert, drawer nowhere. Expanding the origin row whenever such a decision
   * is pending is the page-level half of "the origin must be mounted";
   * idempotent (a `Set` add) and gated exactly like the pane's own hook
   * (save mode, sequence target, library origin, not a sender save), so a
   * delete or a segmentation save never touches a sequence row.
   */
  const pendingLibrarySequenceDecisionId =
    pendingUsageSidebar !== null &&
    pendingUsageSidebar.decision !== null &&
    pendingUsageSidebar.mode === 'save' &&
    pendingUsageSidebar.origin.kind === 'library' &&
    pendingUsageSidebar.target.kind === 'sequence' &&
    pendingUsageSidebar.draftSettings === undefined
      ? pendingUsageSidebar.origin.id
      : null;
  useEffect(() => {
    if (pendingLibrarySequenceDecisionId === null) return;
    const id = pendingLibrarySequenceDecisionId;
    setExpandedSequenceIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, [pendingLibrarySequenceDecisionId]);
  /**
   * U6 (S2) — this page's hook now owns THREE kinds of pending request:
   *  - any `delete`-mode request (unchanged — a delete can be requested for
   *    any row on this page);
   *  - a `save`-mode request for a SEGMENTATION opened from a LIBRARY origin
   *    (this page's own condition editors); and
   *  - Playbooks QA round 6, U7 (item 3) — a `save`-mode SETTINGS request
   *    (`draftSettings !== undefined`, plan 2026-09-28-006 U4) for a SEQUENCE
   *    opened from a LIBRARY origin (this page's row's `SequenceSenderPopover`).
   *
   * The plain-sequence-save branch is STILL narrowed away on purpose: an
   * ordinary steps `save` on a SEQUENCE from a library origin belongs to
   * that row's own `SequenceCopyPane` hook, which mounts `updateAll`/
   * `saveNew` handlers of its own for STEPS — consuming it here would run
   * the wrong write. A SENDER pending shares that exact same origin key
   * (`library:sequence:<id>`), which is why `SequenceCopyPane.tsx` itself
   * has a matching exclusion (see that file's own doc) — the two hooks
   * divide a shared key by `draftSettings`'s presence, never both consuming
   * it.
   */
  const configOriginKey =
    pendingUsageSidebar === null
      ? ''
      : pendingUsageSidebar.mode === 'delete' ||
          (pendingUsageSidebar.origin.kind === 'library' &&
            pendingUsageSidebar.target.kind === 'segmentation') ||
          (pendingUsageSidebar.origin.kind === 'library' &&
            pendingUsageSidebar.target.kind === 'sequence' &&
            pendingUsageSidebar.draftSettings !== undefined)
        ? originKeyOf(pendingUsageSidebar.origin)
        : '';
  useUsageSidebarDecision(configOriginKey, {
    delete: (pending) => handleDeleteConfirm(pending.target.kind, pending.target.id),
    updateAll: (pending, isStillCurrent) =>
      pending.draftSettings !== undefined
        ? handleSettingsUpdateAll(pending, isStillCurrent)
        : handleSegmentationUpdateAll(pending, isStillCurrent),
    saveNew: (pending, isStillCurrent) =>
      pending.draftSettings !== undefined
        ? handleSettingsSaveNew(pending, isStillCurrent)
        : handleSegmentationSaveNew(pending, isStillCurrent),
  });

  /**
   * Round-3-QA-fixes plan, Unit C4 (decisions 2/8/9) — fetch-before-open,
   * same pattern as the save gates: a Sequence/Segmentation delete needs the
   * FRESH "where is this used" list before it can say anything true, so the
   * usage fetch happens first and a failure skips the confirm entirely
   * rather than opening one that can't back up what it's about to claim. A
   * Playbook delete has no places list of its own (decision 8 — a
   * playbook's "places" are plays, not rungs) and skips the fetch, keeping
   * its own small `AlertDialog` untouched by U2b (coding-standards.md §5,
   * "Do NOT Create").
   *
   * U2b — for Sequence/Segmentation, this now opens the SAME sidebar a
   * save-gate does (`mode: 'delete'`), including the zero-places case
   * (builder's call: consistency — every non-Playbook delete always goes
   * through the sidebar, never a separate zero-places dialog).
   */
  const handleDeleteRequest = useCallback(
    async (kind: PlaybooksConfigRowKind, id: string, name: string) => {
      if (kind === 'playbook') {
        openAlertDialog({
          size: 'sm',
          content: (
            <>
              <AlertDialogHeader onClose={closeAlertDialog}>
                <AlertDialogTitle>Delete "{name}"?</AlertDialogTitle>
              </AlertDialogHeader>
              <AlertDialogBody className="flex flex-col gap-3">
                <AlertDialogDescription>
                  Plays already using it keep working. This can&apos;t be undone.
                </AlertDialogDescription>
              </AlertDialogBody>
              <AlertDialogActions>
                <Button variant="secondary" onClick={closeAlertDialog}>
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    handleDeleteConfirm('playbook', id)
                      .then(closeAlertDialog)
                      .catch((err: unknown) =>
                        addErrorToast({
                          content: err instanceof Error ? err.message : 'Could not delete — try again.',
                        }),
                      );
                  }}
                >
                  Delete
                </Button>
              </AlertDialogActions>
            </>
          ),
        });
        return;
      }

      let places: PlaybooksUsagePlace[] = [];
      let ownVersionPlays: PlaybooksUsagePlay[] = [];
      try {
        const usage = await queryClient.fetchQuery({
          queryKey: kind === 'sequence' ? sequenceUsageQueryKey(id) : segmentationUsageQueryKey(id),
          queryFn: () => (kind === 'sequence' ? getSequenceUsage(apiFetch, id) : getSegmentationUsage(apiFetch, id)),
          staleTime: 0,
        });
        places = usage.places;
        ownVersionPlays = usage.ownVersionPlays ?? [];
      } catch {
        addErrorToast({ content: "Couldn't check where this is used — try again." });
        return;
      }

      openUsageSidebar({
        mode: 'delete',
        // Plan 2026-09-28-006 U4 — the Library page lists Library rows only
        // (never a play's copy, never an archived piece).
        target: { kind, id, name, owningPlayId: null, archivedInLibrary: false },
        origin: { kind: 'library', targetKind: kind, id },
        draftSteps: null,
        headVersionId: null,
        places,
        ownVersionPlays,
        // A delete pending has no Save menu; ③'s wording never applies.
        templatePlaybooks: [],
        acknowledgedCount: places.length,
      });
    },
    [apiFetch, queryClient, openAlertDialog, closeAlertDialog, handleDeleteConfirm],
  );

  const menuItemsFor = useCallback(
    (kind: PlaybooksConfigRowKind, id: string, name: string, usageCount?: number) => {
      const descriptors = getConfigRowMenuItems(kind, { isAdmin });
      const iconByAction = {
        edit: PencilSimple,
        rename: TextAa,
        duplicate: CopySimple,
        delete: TrashSimple,
      } as const;
      const onSelectByAction: Record<PlaybooksConfigRowAction, () => void> = {
        edit: () => handleEdit(id),
        rename: () => setRenameTarget({ kind, id, name, usageCount: kind === 'sequence' ? usageCount : undefined }),
        duplicate: () => handleDuplicate(kind, id),
        // `handleDeleteRequest` is async (Unit C4 — fetch-before-open usage
        // check) where the sibling actions above are not; `void` marks the
        // promise as deliberately unawaited here (oxlint `no-misused-promises`).
        delete: () => void handleDeleteRequest(kind, id, name),
      };
      return descriptors.map((d) => ({
        key: d.action,
        label: d.label,
        icon: iconByAction[d.action],
        danger: d.danger,
        onSelect: onSelectByAction[d.action],
      }));
    },
    [isAdmin, handleEdit, handleDuplicate, handleDeleteRequest],
  );

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center p-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (query.isError || !filtered) {
    return (
      <div className="px-8 py-8">
        <Alert status="error">Failed to load the library. Please try again.</Alert>
      </div>
    );
  }

  return (
    <div className="flex-1 px-8 py-8">
      {/*
        Round-4 U7 (E3) — this page's condition-editor dirt is
        `dirtySegmentationIds`, component-local `useState` rather than a
        module-scope store, so it is passed in as a boolean instead of being
        lifted (Decision 12). Stated consequence: a condition draft here still
        dies on an unmount, so the guard covers header-link clicks, this
        page's own imperative navigations (`guardedNavigate`), and tab
        close/refresh, NOT the Back button — unlike the play flow, whose
        drafts now survive it.

        `configDirty` (not the raw set) — round-4 QA codex-review fix, wave 1,
        finding 3: this page's OTHER dirt lives in the shared copy-draft
        store, via `SequenceCopyPane`. `draftsSurvive={false}` — finding 4:
        the accepted gap above is real, so the dialog must not offer "Leave,
        keep edits" or claim the edits belong to "this Play".
      */}
      <UnsavedWorkGuard dirty={configDirty} draftsSurvive={false} discardScope="library" />
      <div className="mb-2">
        <Heading size="xl">Library</Heading>
      </div>

      <div className="mt-6 mb-6 max-w-sm">
        <DebouncedSearchInput
          aria-label="Search the library"
          placeholder="Search Playbooks, Plays, Segments, Sequences…"
          initialValue={search}
          onSearch={setSearch}
        />
      </div>

      <div className="flex flex-col gap-8">
        <SectionShell
          title="Playbooks"
          count={filtered.playbooks.length}
          create={{ label: 'New Playbook', onClick: () => setCreateKind('playbook') }}
        >
          {filtered.playbooks.length === 0 ? (
            <EmptySectionRow hasSearch={search.trim().length > 0} kind="Playbooks" />
          ) : (
            <div className="divide-y divide-border-secondary">
              {filtered.playbooks.map((pb: PlaybooksPlaybookSummary) => (
                <RowShell
                  key={pb.id}
                  name={pb.name}
                  badges={
                    <>
                      <Badge color={configRowKindBadgeColor('Playbook')}>Playbook</Badge>
                      <Badge>{pb.rungCount} segment{pb.rungCount === 1 ? '' : 's'}</Badge>
                    </>
                  }
                  menu={<KebabMenu items={menuItemsFor('playbook', pb.id, pb.name)} ariaLabel={`More actions for ${pb.name}`} />}
                />
              ))}
            </div>
          )}
        </SectionShell>

        <SectionShell
          title="Plays"
          icon={PaperPlaneTilt}
          count={filteredPlays.reduce((n, group) => n + group.plays.length, 0)}
        >
          {playsLibrary.isLoading ? (
            <div className="flex justify-center p-6">
              <LoadingSpinner size="sm" />
            </div>
          ) : playsLibrary.isError ? (
            <div className="p-4">
              <Alert status="error">Couldn&apos;t load plays. Please try again.</Alert>
            </div>
          ) : (
            <PlaysLibrarySection
              groups={filteredPlays}
              playbookNameById={playbookNameById}
              basePath={basePath}
              hasSearch={search.trim().length > 0}
              onOpen={guardedNavigate}
            />
          )}
        </SectionShell>

        <SectionShell
          title="Segments"
          icon={Funnel}
          count={filtered.segmentations.length}
          create={{ label: 'New Segment', onClick: () => setCreateKind('segmentation') }}
        >
          {filtered.segmentations.length === 0 ? (
            <EmptySectionRow hasSearch={search.trim().length > 0} kind="Segments" />
          ) : (
            <div className="divide-y divide-border-secondary">
              {filtered.segmentations.map((seg: PlaybooksSegmentationSummary) => {
                const expanded = expandedSegmentationIds.has(seg.id);
                return (
                  <RowShell
                    key={seg.id}
                    name={seg.name}
                    badges={
                      <>
                        <Badge color={configRowKindBadgeColor('Segment')}>Segment</Badge>
                        <Badge>{usagePlacesBadgeText(seg.usageCount)}</Badge>
                        {seg.isMapped ? (
                          <StatusBadge status="success">Default sequence set</StatusBadge>
                        ) : (
                          <>
                            <StatusBadge status="neutral">No default sequence</StatusBadge>
                            <Text size="xs" variant="tertiary">
                              No default sequence — people who land here get nothing.
                            </Text>
                          </>
                        )}
                      </>
                    }
                    menu={
                      <KebabMenu
                        items={menuItemsFor('segmentation', seg.id, seg.name)}
                        ariaLabel={`More actions for ${seg.name}`}
                      />
                    }
                    expand={{
                      expanded,
                      onToggleExpand: () => toggleSegmentationExpanded(seg.id),
                      body: expanded ? (
                        <div className="flex flex-col gap-2">
                          {/* Named narrowing (Spencer-confirmed 2026-08-31): Save/Revert
                              only. The prototype's `libTgRow` toolbar also has an
                              admin-only "Preview impact of vN" button
                              (`playbooks-prototype-v6.html:5235-5241`) — deferred, it
                              needs campaign-member-matching context this page doesn't
                              have wired. Not built here; see this unit's Deferred To
                              Follow-Up notes. */}
                          <div className="flex justify-end">
                            <Button
                              variant="inline-text"
                              size="xs"
                              disabled={!dirtySegmentationIds.has(seg.id)}
                              onClick={() => revertSegmentationConditions(seg.id)}
                            >
                              Revert
                            </Button>
                          </div>
                          <SegmentationConditionsEditor
                            key={`${seg.id}:${segmentationEditorNonce[seg.id] ?? 0}`}
                            segmentationId={seg.id}
                            onDirtyChange={(dirty) => handleSegmentationDirtyChange(seg.id, dirty)}
                            // U6 (S2) — the library doorway to the same gate;
                            // this page's own hook (above) executes the
                            // decision, forking without a rung to remap.
                            usageOrigin={{
                              kind: 'library',
                              targetKind: 'segmentation',
                              id: seg.id,
                            }}
                          />
                        </div>
                      ) : null,
                    }}
                  />
                );
              })}
            </div>
          )}
        </SectionShell>

        <SectionShell
          title="Sequences"
          icon={Envelope}
          count={filtered.sequences.length}
          create={{ label: 'New Sequence', onClick: () => setCreateKind('sequence') }}
        >
          {filtered.sequences.length === 0 ? (
            <EmptySectionRow hasSearch={search.trim().length > 0} kind="Sequences" />
          ) : (
            <div className="divide-y divide-border-secondary">
              {filtered.sequences.map((seq: PlaybooksSequenceSummary) => {
                const expanded = expandedSequenceIds.has(seq.id);
                return (
                  <RowShell
                    key={seq.id}
                    name={seq.name}
                    badges={
                      <>
                        <Badge color={channelBadgeColor(seq.channel)}>{channelLabel(seq.channel)}</Badge>
                        <Badge>{seq.stepCount} step{seq.stepCount === 1 ? '' : 's'}</Badge>
                        <Badge>{usagePlacesBadgeText(seq.usageCount)}</Badge>
                        {/*
                          U9 (terra-l2ai-epic-076.15) — the library's second
                          "Generate with AI" entry point: a row trigger,
                          visible only once expanded (the pane below is where
                          the rep actually reads the resulting review). Opens
                          a brand-new session with an EMPTY seed — U9's own
                          preselect (the sequence's `generationPreset`) is
                          `GenerateCampaignPicker`'s job (it has the sequence
                          detail fetch this row doesn't), not this click's.
                          Segmentation/Playbook rows never get this badge
                          (Key Decision 13).
                        */}
                        {expanded && (
                          // R5-8 (playbooks-qa-round5-feedback.md) — the
                          // purple accent needs BOTH the token-remap wrapper
                          // AND `variant="primary"` (the accent remaps the
                          // tokens `variant="primary"` reads; `secondary`
                          // never reads them, and the wrapper alone with
                          // `secondary` stays grey). Mirrors
                          // `SequencesStep.tsx`'s play-flow header trigger,
                          // scoped to just this button (not the whole badge
                          // row) so `SequenceSenderPopover`'s sibling button
                          // stays un-purple.
                          <div className={GENERATE_AI_BUTTON_WRAPPER_CLASS}>
                            <Button
                              variant="primary"
                              size="xs"
                              onClick={() => {
                                // D6/C1 — a session for this exact sequence
                                // survives across the row's collapse/expand
                                // (R13) and auto-reopens the drawer on the
                                // fresh mount, restored from its own staged
                                // snapshot (`GenerateWithAIDrawer`'s own
                                // restore effect). Re-clicking this trigger
                                // while that's already open would blow the
                                // restore away with a blank `openGenerateSession`
                                // reseed — read the live session directly
                                // (not a hook subscription — this is a click
                                // handler, not a render) and no-op instead.
                                const current = useGenerateWithAIStore.getState().session;
                                if (current?.scope === 'library' && current.sequenceId === seq.id) return;
                                openGenerateSession({
                                  scope: 'library',
                                  sequenceId: seq.id,
                                  surface: 'library',
                                  seed: {},
                                });
                              }}
                            >
                              <Icon type={MagicWand} />
                              Generate with AI
                            </Button>
                          </div>
                        )}
                      </>
                    }
                    /*
                      U9 (SN1) / Decision 3 — sender is a SEQUENCE-identity
                      field now, and this row is the ONE place it is set.
                      Read-only label plus the popover that writes
                      `default_sender`; the trigger carries
                      `data-row-control` so opening it never also toggles the
                      row (`RowShell`'s own header-click guard).
                    */
                    subline={
                      <>
                        <Text size="xs" variant="tertiary">
                          Sender: {senderLabel(seq.sender)}
                        </Text>
                        <SequenceSenderPopover
                          sequenceId={seq.id}
                          currentSender={seq.sender}
                          // R5-5 — already-loaded library summary field, no
                          // extra fetch; drives `usedInPlacesText`.
                          usageCount={seq.usageCount}
                          usageOrigin={{ kind: 'library', targetKind: 'sequence', id: seq.id }}
                          sequenceName={seq.name}
                          // Optimistic row patch, mirroring
                          // `updateLibraryRowName`'s own shape: the popover
                          // already invalidates `library`, but a refetch is a
                          // round trip and the rep just told us the answer.
                          onSaved={(sender) => updateLibrarySequenceRow(seq.id, { sender })}
                        />
                        {/* playbooks-inflection-wiring (2026-09-17) — the flow-ID
                            fix: Gong keeps its existing browse-panel doorway
                            elsewhere on this row; every OTHER channel gets this
                            free-text popover, since `sequence-readiness.ts`
                            requires a flow on every channel and there was
                            previously no way for a non-Gong sequence to satisfy
                            it (see TL;DR item 1 of that plan). */}
                        {seq.channel !== 'gong_engage' && (
                          <>
                            <Text size="xs" variant="tertiary">
                              {`Flow ID: ${seq.flowId ?? 'Not set'}`}
                            </Text>
                            <SequenceFlowIdPopover
                              sequenceId={seq.id}
                              channel={seq.channel}
                              currentFlowId={seq.flowId}
                              usageCount={seq.usageCount}
                              onSaved={(flowId) => updateLibrarySequenceRow(seq.id, { flowId })}
                            />
                          </>
                        )}
                        {seq.channel === 'inflection' && (
                          <>
                            <Text size="xs" variant="tertiary">
                              {`CC: ${senderLabel(seq.cc)} · Reply-to: ${senderLabel(seq.replyTo)}`}
                            </Text>
                            <SequenceInflectionPopover
                              sequenceId={seq.id}
                              currentCc={seq.cc}
                              currentReplyTo={seq.replyTo}
                              usageCount={seq.usageCount}
                              onSaved={(fields) => updateLibrarySequenceRow(seq.id, fields)}
                            />
                          </>
                        )}
                      </>
                    }
                    menu={
                      <KebabMenu
                        items={menuItemsFor('sequence', seq.id, seq.name, seq.usageCount)}
                        ariaLabel={`More actions for ${seq.name}`}
                      />
                    }
                    expand={{
                      expanded,
                      onToggleExpand: () => toggleSequenceExpanded(seq.id),
                      body: expanded ? (
                        <SequenceCopyPane
                          sequenceId={seq.id}
                          channel={seq.channel}
                          onSavedAsNewCopy={(newSequenceId, summary) =>
                            handleSequenceSavedAsNewCopy(seq.id, newSequenceId, summary)
                          }
                        />
                      ) : null,
                    }}
                  />
                );
              })}
            </div>
          )}
        </SectionShell>
      </div>

      {renameTarget && (
        <PushDrawer
          key={renameTarget.id}
          title={`Rename ${renameTarget.name}`}
          onClose={() => setRenameTarget(null)}
        >
          <NameDrawerField
            initialName={renameTarget.name}
            saving={savingRename}
            onSave={handleRenameSave}
            onCancel={() => setRenameTarget(null)}
            warning={
              renameTarget.kind === 'sequence' ? renameWarningText(renameTarget.usageCount ?? 0) : null
            }
          />
        </PushDrawer>
      )}

      {createKind && (
        <PushDrawer
          key={`create-${createKind}`}
          title={
            createKind === 'segmentation'
              ? 'New Segment'
              : createKind === 'sequence'
                ? 'New Sequence'
                : 'New Playbook'
          }
          onClose={() => setCreateKind(null)}
        >
          {createKind === 'sequence' ? (
            <SequenceCreateFields
              saving={savingCreate}
              onSave={handleCreateSave}
              onCancel={() => setCreateKind(null)}
            />
          ) : (
            <NameDrawerField
              initialName=""
              saving={savingCreate}
              onSave={handleCreateSave}
              onCancel={() => setCreateKind(null)}
            />
          )}
        </PushDrawer>
      )}
    </div>
  );
}
